import { Box, Typography, TextField, Button, FormControlLabel, Switch, CircularProgress, Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";

import Api from "../../api/Api";
import { Urls } from "../../api/Urls";
import FormikErrorText from "../../components/ui/FormikErrorText";

/**
 * Makeup-policy settings panel.
 * Backend: GET /makeup-policy, PATCH /makeup-policy
 * Driven by the MakeupPolicyService — keys and defaults live server-side.
 */
function MakeupPolicySettings({ access_token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initial, setInitial] = useState({
    auto_approve: false,
    min_hours_before: 24,
    max_per_term: 3,
    refund_threshold_hours: 24,
    proposal_slot_count: 5,
    request_window_days: 14,
  });

  const headers = {
    "content-type": "application/json",
    authorization: `Bearer ${access_token}`,
  };

  const fetchPolicy = () => {
    setLoading(true);
    Api.get(Urls.GET_MAKEUP_POLICY, { headers })
      .then((res) => {
        const d = res?.data?.data || res?.data || {};
        setInitial({
          auto_approve: !!d.auto_approve,
          min_hours_before: Number(d.min_hours_before ?? 24),
          max_per_term: Number(d.max_per_term ?? 3),
          refund_threshold_hours: Number(d.refund_threshold_hours ?? 24),
          proposal_slot_count: Number(d.proposal_slot_count ?? 5),
          request_window_days: Number(d.request_window_days ?? 14),
        });
        setLoading(false);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to load makeup policy");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchPolicy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validationSchema = yup.object({
    auto_approve: yup.boolean(),
    min_hours_before: yup.number().min(0).required("Required"),
    max_per_term: yup.number().min(0).required("Required"),
    refund_threshold_hours: yup.number().min(0).required("Required"),
    proposal_slot_count: yup.number().min(1).max(50).required("Required"),
    request_window_days: yup.number().min(0).required("Required"),
  });

  const handleSave = (values) => {
    setSaving(true);
    Api.patch(Urls.UPDATE_MAKEUP_POLICY, values, { headers })
      .then(() => {
        toast.success("Makeup policy updated");
        setSaving(false);
        fetchPolicy();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to update makeup policy");
        setSaving(false);
      });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" padding="40px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <Box>
        <Typography fontSize="18px" fontWeight="500" lineHeight="24px" color="#4B465C">
          Makeup Policy
        </Typography>
        <Typography fontSize="13px" color="#7B7A86" sx={{ mt: "4px" }}>
          Controls how player makeup requests are validated, proposed and refunded.
        </Typography>
      </Box>

      <Formik
        initialValues={initial}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSave}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography fontSize="13px" color="#4B465C" sx={{ mb: "4px" }}>
                  Min hours before session (cancel cutoff)
                </Typography>
                <Field name="min_hours_before" type="number" as={TextField} fullWidth size="small" />
                <ErrorMessage component={FormikErrorText} name="min_hours_before" />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography fontSize="13px" color="#4B465C" sx={{ mb: "4px" }}>
                  Max makeup requests per player per term
                </Typography>
                <Field name="max_per_term" type="number" as={TextField} fullWidth size="small" />
                <ErrorMessage component={FormikErrorText} name="max_per_term" />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography fontSize="13px" color="#4B465C" sx={{ mb: "4px" }}>
                  Refund threshold (hours before session)
                </Typography>
                <Field name="refund_threshold_hours" type="number" as={TextField} fullWidth size="small" />
                <ErrorMessage component={FormikErrorText} name="refund_threshold_hours" />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography fontSize="13px" color="#4B465C" sx={{ mb: "4px" }}>
                  Proposal slot count (sessions offered to player)
                </Typography>
                <Field name="proposal_slot_count" type="number" as={TextField} fullWidth size="small" />
                <ErrorMessage component={FormikErrorText} name="proposal_slot_count" />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography fontSize="13px" color="#4B465C" sx={{ mb: "4px" }}>
                  Request window (days after the missed session)
                </Typography>
                <Field name="request_window_days" type="number" as={TextField} fullWidth size="small" />
                <ErrorMessage component={FormikErrorText} name="request_window_days" />
              </Grid>

              <Grid item xs={12} sm={6} display="flex" alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!values.auto_approve}
                      onChange={(e) => setFieldValue("auto_approve", e.target.checked)}
                    />
                  }
                  label="Auto-approve eligible requests"
                />
              </Grid>

              <Grid item xs={12}>
                <Button variant="primary_active" type="submit" disabled={saving} sx={{ height: "38px" }}>
                  {saving ? "Saving…" : "Save policy"}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
}

export default MakeupPolicySettings;
